import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { pdApi } from '../api';
import './QuickStartAnalysis.css';
/* The body of this screen IS the review tree — the source includes the same partial here as
   on the review screen, so the faithful clone is one component used twice. */
import { QuickStartReviewBody } from './QuickStartReviewBody';
import { HelpPanel } from '../HelpPanel';
import { QUICK_START_ANALYSIS } from '../helpContent';
import { useQuickStartTree } from './useQuickStartTree';

/**
 * Quick Start Analysis.
 *
 * §5 of `project-management-module-build-instructions.md`, cloned from
 * `views/roadmap-execution/quick-start-analysis.ejs`.
 *
 * ── WHY IT EXISTS ──────────────────────────────────────────────────────────────────────
 *
 * The review screen shows one run as a single stacked list. At 90 projects and 172
 * initiatives — this platform's own run 8 — that is unusable: you cannot find the slice you
 * care about, and the actions scroll off the top. This screen INTERROGATES a run instead of
 * scrolling it.
 *
 * ── FILTERING IS SERVER-SIDE, AND THE URL CARRIES IT ───────────────────────────────────
 *
 * `?run=&projects=&opps=` are read by the server, which returns only the chosen slice. Two
 * consequences, both deliberate: a view is a URL you can keep or send to someone, and a
 * 90-project proposal is never shipped to the browser just to be narrowed. It also means the
 * back button works, which a client-side filter would not give.
 *
 * The pickers are cascading but the server builds the TREE from the UNFILTERED proposal, so
 * narrowing to one project never empties the pulldown you would need to get back.
 *
 * ── THE THREE PROJECT STATES ARE NOT TWO ───────────────────────────────────────────────
 *
 *   absent      not chosen yet — show nothing. A 90-project wall on arrival is not a
 *               starting point, and this is a different thing from "matched nothing".
 *   __ALL__     the deliberate choice to see everything.
 *   a list      those projects.
 *
 * Each has its own empty state, because telling someone "nothing matches" when they simply
 * have not picked yet sends them looking for a fault that is not there.
 */

type Dict = Record<string, unknown>;
const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));


interface TreeOpp {
  ref: string;
  code: string;
  name: string;
}
interface TreeProject {
  ref: string;
  code: string;
  name: string;
  opps: TreeOpp[];
}
interface Payload {
  runs: Dict[];
  run: Dict | null;
  tree: TreeProject[];
  programs: Dict[];
  totals: { projects: number; opps: number; items: number };
  alreadyGenerated: boolean;
  selection: {
    programs: string[];
    opps: string[];
    delivs: string[];
    team: string[];
    globals: string[];
  };
  selProjects: string[];
  selOpps: string[];
  chosenAll: boolean;
  nothingChosen: boolean;
}

const ALL = '__ALL__';

/* This screen reads a run; it does not edit one. */
const EMPTY_SET: Set<string> = new Set();
const EMPTY_DUP = { programs: {}, opportunities: {} };
const EMPTY_RES: Record<string, string> = {};
const NOOP = () => undefined;

export function QuickStartAnalysis() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [drawer, setDrawer] = useState(false);

  /*
   * LOCK THE HEADER, THE FILTERS AND THE ACTION ROW; SCROLL ONLY THE DATA.
   *
   * Owner, 2026-08-17: "I want the screen header and all the filter fields and generate
   * selected button to be locked then only scroll the data that are within data section."
   *
   * The height is MEASURED, never declared. `height: 100%` does not work in this shell —
   * `<main>` carries `min-height: 100vh` and grows with its content, so a percentage
   * resolves against nothing and the window scrolls. A typed constant would be worse: it
   * would encode the header's current height and break the moment a filter wraps to a
   * second line.
   *
   * A CALLBACK REF, not `useRef`: this page renders a loading gate first, so an effect keyed
   * on a ref object would run once against null and silently never apply a height.
   */
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);

  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      /* Ignore sub-pixel churn, or the observer and the height retrigger each other. */
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  /*
   * SELECTION AND DRAG — the screen used to pass stubs for all nine interactive props, so
   * every checkbox was permanently unchecked and every drag was refused. The source's
   * analysis screen is fully interactive (`quick-start-analysis.ejs:191-206` plus the
   * `_quick-start-review` partial); see useQuickStartTree.ts for the whole account.
   */
  const picker = useQuickStartTree({
    programs: (data?.programs as Dict[]) ?? [],
    proposal: (data?.run?.proposal as Dict) ?? null,
    /*
     * THE TREE DOES NOT RENDER FROM THE PROPOSAL ON THIS SCREEN, and that is why a drop
     * appeared to do nothing: the item dragged, the phase highlighted, and it snapped back.
     *
     * On Preview, `programs` IS derived from `proposal`, so the drop handler's optimistic
     * rewrite re-renders the tree. Here the server sends a SEPARATE, filtered `programs`
     * array alongside `run.proposal`, and the body renders from `programs`. Updating only
     * the proposal wrote the move into a field nothing on screen reads.
     *
     * So patch both: the proposal (what gets persisted and re-read) and the matching entries
     * in `programs` (what is on screen). Only programs present in the new proposal are
     * replaced, so the current filter is preserved rather than being widened by a drag.
     */
    setProposal: (next) =>
      setData((cur) => {
        if (!cur || !cur.run) return cur;
        const fresh = new Map(
          (((next?.programs as Dict[]) ?? []) as Dict[]).map((p) => [s(p.ref ?? p.code), p]),
        );
        return {
          ...cur,
          run: { ...cur.run, proposal: next },
          programs: cur.programs.map((p) => fresh.get(s(p.ref ?? p.code)) ?? p),
        } as Payload;
      }),
    runId: data?.run ? Number(data.run.id) : null,
    setError,
  });

  /* The two pulldowns are the source's own control — a closed button that opens a floating
     list — so their open state lives here, and a click anywhere else closes them. */
  const [projOpen, setProjOpen] = useState(false);
  const [oppOpen, setOppOpen] = useState(false);
  const projRef = useRef<HTMLDivElement | null>(null);
  const oppRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (projRef.current && !projRef.current.contains(t)) setProjOpen(false);
      if (oppRef.current && !oppRef.current.contains(t)) setOppOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const [busy, setBusy] = useState(false);

  const runParam = params.get('run') ?? '';
  const projectsParam = params.get('projects') ?? '';
  const oppsParam = params.get('opps') ?? '';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = new URLSearchParams();
      if (runParam) q.set('run', runParam);
      if (projectsParam) q.set('projects', projectsParam);
      if (oppsParam) q.set('opps', oppsParam);
      const d = (await pdApi.quickStart.analysis(q.toString())) as unknown as Payload & {
        success: boolean;
        error?: string;
      };
      if (!d.success) throw new Error(d.error ?? 'Could not load the analysis');
      setData(d);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [runParam, projectsParam, oppsParam]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Change one query parameter, and clear what it invalidates downstream. */
  const setParam = (key: 'run' | 'projects' | 'opps', value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    /* Changing the run invalidates both narrowings; changing the projects invalidates the
       initiative choice, which was drawn from them. */
    if (key === 'run') {
      next.delete('projects');
      next.delete('opps');
    }
    if (key === 'projects') next.delete('opps');
    setParams(next);
    setNotice(null);
  };

  /* Memoised so the `oppOptions` dependency list is stable — a fresh `[]` on every render
     would rebuild the initiative list continuously. */
  const tree = useMemo(() => data?.tree ?? [], [data]);
  const selProjects = useMemo(() => data?.selProjects ?? [], [data]);
  const selOpps = useMemo(() => data?.selOpps ?? [], [data]);

  /* The initiative list is REBUILT from the chosen projects rather than hidden with
     `option[hidden]`, which behaves inconsistently across browsers — §5's own note. */
  const oppOptions = useMemo(() => {
    const chosen = data?.chosenAll
      ? tree
      : tree.filter((t) => selProjects.includes(t.code));
    return chosen.flatMap((t) => t.opps.map((o) => ({ ...o, project: t.code })));
  }, [tree, selProjects, data?.chosenAll]);

  /** Ctrl/Cmd-click adds or removes; a plain click replaces. The source's `qaPick`. */
  const togglePick = (list: readonly string[], val: string): string[] => {
    const i = list.indexOf(val);
    return i >= 0 ? list.filter((x) => x !== val) : [...list, val];
  };

  /* The captions, word for word from qaRenderProjects / qaRenderOpps. */
  const projCaption = data?.chosenAll
    ? '— All projects —'
    : !selProjects.length
      ? '— Select a project —'
      : selProjects.length === 1
        ? selProjects[0]!
        : `${selProjects.length} projects selected`;
  const noParent = !data?.chosenAll && !selProjects.length;
  const oppCaption = noParent
    ? '— Select a project first —'
    : !selOpps.length
      ? '— All initiatives —'
      : selOpps.length === 1
        ? selOpps[0]!
        : `${selOpps.length} initiatives selected`;

  /* The tree hides anything not in `visible`. The SERVER has already applied this screen's
     filters, so everything it returned is visible — otherwise the tree would filter a second
     time against a set it knows nothing about and render an empty body. */
  const visibleAll = useMemo(() => {
    const progs = new Set<string>();
    const opps = new Set<string>();
    for (const pg of (data?.programs as Dict[]) ?? []) {
      const pid = s(pg.ref || pg.code);
      progs.add(pid);
      for (const o of ((pg.opportunities as Dict[]) ?? [])) {
        opps.add(`${pid}|${s(o.ref || o.code)}`);
      }
    }
    return { progs, opps };
  }, [data]);

  const generate = async () => {
    if (!data?.run || !data.selection.programs.length) return;
    const t = data.totals;
    if (
      !window.confirm(
        `Generate ${t.projects} project(s), ${t.opps} initiative(s) and ${t.items} work item(s)?\n\n` +
          'Records are created UNROUTED — the delivery pathway is chosen later on Delivery ' +
          'Pathway. This can be undone from the run.',
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      /*
       * THE SELECTION MAP IS THE SERVER'S — built from the same objects it rendered, so its
       * indices match the stored proposal. The generate endpoint's map is opt-OUT, so this
       * sends `false` for everything the server did NOT include in the slice.
       */
      const keep = new Set([
        ...data.selection.programs,
        ...data.selection.opps,
        ...data.selection.delivs,
        ...data.selection.globals,
        ...data.selection.team,
      ]);
      const selection: Record<string, boolean> = {};
      const full = (await pdApi.quickStart.load(Number(data.run.id))) as Dict;
      for (const pg of ((full.proposal as Dict)?.programs as Dict[]) ?? []) {
        const pref = s(pg.ref);
        if (!keep.has(pref)) selection[pref] = false;
        ((pg.global_deliverables as Dict[]) ?? []).forEach((_g, i) => {
          if (!keep.has(`${pref}|${i}`)) selection[`${pref}:global:${i}`] = false;
        });
        for (const t of (pg.team as Dict[]) ?? []) {
          if (!keep.has(s(t.ref))) selection[`${pref}:team:${s(t.name)}`] = false;
        }
        for (const o of (pg.opportunities as Dict[]) ?? []) {
          const oref = s(o.ref);
          if (!keep.has(`${pref}|${oref}`)) selection[oref] = false;
          const dl = ((pg.deliverables_by_opp as Record<string, Dict[]>) ?? {})[oref] ?? [];
          dl.forEach((d, i) => {
            if (!keep.has(`${pref}|${oref}|${i}`)) selection[`${oref}:d:${s(d.name)}`] = false;
          });
        }
      }
      const r = await pdApi.quickStart.generate({
        run_id: Number(data.run.id),
        selection,
        resolutions: {},
      });
      const res = (r.result ?? {}) as Record<string, number | string | null>;
      setNotice(
        `✓ Generated ${res.projects ?? 0} project(s), ${res.opportunities ?? 0} opportunit(y/ies) ` +
          `and ${res.deliverables ?? 0} work item(s).${res.note ? ` ${res.note}` : ''}`,
      );
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const purge = async () => {
    if (!data?.run) return;
    const typed = window.prompt(
      'This permanently deletes ALL records this run created (projects, opportunities, work items). Type DELETE to confirm.',
    );
    if (!typed || typed.trim().toUpperCase() !== 'DELETE') return;
    setBusy(true);
    try {
      await pdApi.quickStart.purge(Number(data.run.id));
      setNotice('Generated data deleted. The run can be generated again.');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const t = data?.totals ?? { projects: 0, opps: 0, items: 0 };
  const refFiles = (data?.run?.reference_files as { name: string; purpose: string }[] | null) ?? [];

  return (
    <div className="qa-page" ref={setRootEl} style={rootH ? { height: rootH } : undefined}>
      {/* ── Anchored header. Nothing in here scrolls. ──────────────────────────────── */}
      <div className="qa-head">
        <h1 className="page-header__title">Program Quick Start — Analysis</h1>
        <p className="qa-sub">
          Pick the run, narrow to the projects and initiatives you care about, then generate
          exactly that slice.
        </p>
        {/* The source carries `qsaHelp` on this screen; ours had no way in at all. It sits
            in the anchored header so it is reachable before a run is chosen. */}
        <div className="qa-help">
          <HelpPanel help={QUICK_START_ANALYSIS} />
        </div>

        {/*
          THE FILTER ROW IS THE SOURCE'S, NOT A REBUILD OF IT.

          Project and Initiative had been native `<select multiple>` boxes wearing the
          source's `.qa-dd-list` class — permanently-expanded lists where the source has a
          CLOSED button carrying a caption and a chevron that opens a floating list. The info
          control used a `.qa-info` class that does not exist in the source at all.

          Every value is quick-start-analysis.ejs's and can be cited: the row at :168-186,
          `.qa-fld.run` 336px at :14, `.qa-i` at :41-44, `.qa-dd-btn` at :23-27,
          `.qa-dd-list` at :28-30, `.qa-item` at :31-38.
        */}
        <div className="qa-bar">
          <div className="qa-fld run">
            <label className="qa-fld-lbl">Analysis run</label>
            <select
              className="qa-ctl"
              value={runParam || s(data?.run?.id)}
              onChange={(e) => setParam('run', e.target.value)}
            >
              {(data?.runs ?? []).map((r) => (
                <option key={s(r.id)} value={s(r.id)}>
                  #{s(r.id)} · {s(r.file_name)} · {s(r.created)}
                </option>
              ))}
            </select>
          </div>

          {/* Outside the field, between the run and Project — where the source puts it. */}
          <button
            type="button"
            className="qa-i"
            title="Everything about this run"
            onClick={() => setDrawer(true)}
            disabled={!data?.run}
          >
            i
          </button>

          <div className="qa-fld wide">
            <label className="qa-fld-lbl">Project</label>
            <div className={`qa-dd${projOpen ? ' open' : ''}`} ref={projRef}>
              <button
                type="button"
                className="qa-ctl qa-dd-btn"
                onClick={() => {
                  setProjOpen((v) => !v);
                  setOppOpen(false);
                }}
              >
                <span className="cap">{projCaption}</span>
                <i className="material-icons">expand_more</i>
              </button>
              <div className="qa-dd-list">
                <div
                  className={`qa-item all${data?.chosenAll || !selProjects.length ? ' selected' : ''}`}
                  onClick={() => {
                    setParam('projects', ALL);
                    setProjOpen(false);
                  }}
                >
                  — All projects — ({tree.length})
                </div>
                {tree.map((p) => (
                  <div
                    key={p.ref}
                    className={`qa-item${selProjects.includes(p.code) ? ' selected' : ''}`}
                    onClick={(ev) => {
                      /* Plain click selects one; Ctrl/Cmd-click adds or removes — the
                         source's `qaPick`, the same gesture as its other pickers. */
                      const next =
                        ev.ctrlKey || ev.metaKey ? togglePick(selProjects, p.code) : [p.code];
                      setParam('projects', next.join(','));
                      if (!(ev.ctrlKey || ev.metaKey)) setProjOpen(false);
                    }}
                  >
                    <span className="code">{p.code}</span>
                    <span className="nm">{p.name || ''}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="qa-fld wide">
            <label className="qa-fld-lbl">Initiative</label>
            <div className={`qa-dd${oppOpen ? ' open' : ''}`} ref={oppRef}>
              <button
                type="button"
                className="qa-ctl qa-dd-btn"
                disabled={noParent}
                style={noParent ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                onClick={() => {
                  setOppOpen((v) => !v);
                  setProjOpen(false);
                }}
              >
                <span className="cap">{oppCaption}</span>
                <i className="material-icons">expand_more</i>
              </button>
              <div className="qa-dd-list">
                <div
                  className={`qa-item all${selOpps.length ? '' : ' selected'}`}
                  onClick={() => {
                    setParam('opps', '');
                    setOppOpen(false);
                  }}
                >
                  — All initiatives — ({oppOptions.length})
                </div>
                {oppOptions.map((o) => (
                  <div
                    key={`${o.project}-${o.ref}`}
                    className={`qa-item${selOpps.includes(o.code) ? ' selected' : ''}`}
                    onClick={(ev) => {
                      const next =
                        ev.ctrlKey || ev.metaKey ? togglePick(selOpps, o.code) : [o.code];
                      setParam('opps', next.join(','));
                      if (!(ev.ctrlKey || ev.metaKey)) setOppOpen(false);
                    }}
                  >
                    <span className="code">{o.code}</span>
                    <span className="nm">
                      {o.name}
                      {'  ·  in '}
                      {o.project}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="qa-act">
          <span className="qa-sel">
            Selected: <b>{t.projects}</b> project(s) · <b>{t.opps}</b> initiative(s) ·{' '}
            <b>{t.items}</b> work item(s)
          </span>
          {/* `quick-start-analysis.ejs:191-192` — these are qsAll(true|false): they TICK and
              UNTICK the tree. They used to set the project FILTER instead, which is what
              "Reset filters" beside them already does. */}
          <button type="button" className="qa-btn" onClick={() => picker.selectAll(true)}>
            Select all
          </button>
          <button type="button" className="qa-btn" onClick={() => picker.selectAll(false)}>
            Clear all
          </button>
          <button
            type="button"
            className="qa-btn"
            onClick={() => {
              const next = new URLSearchParams();
              if (runParam) next.set('run', runParam);
              setParams(next);
            }}
          >
            Reset filters
          </button>

          {/*
            §19.3, case 1 — THE BUTTON THAT ACTS ON THE CHECKBOXES LIVES HERE, NOT IN THE
            REVIEW LIST. This used to be an if/else that REPLACED Generate with Delete once a
            run had created anything, which is the run-level lock again in a second file.

            §19.2 unlocked the cards below — a project that exists is greyed and ticked, so
            what is left tickable is exactly what has NOT been created yet, typically a new
            initiative under a project generated earlier. Replacing the button left the screen
            able to select work it could not act on: strictly worse than the uniformly dead
            screen it came from, because at least that one said the run was closed.

            So the badge and Delete render IN ADDITION to Generate, never instead of it.
          */}
          {/* The right-hand group, so `margin-left:auto` sits on the GROUP and not on each
              button — three of them each pushing themselves right would spread apart. Cloned
              from quick-start-analysis.ejs:194. */}
          <span className="qa-actgrp">
            {data?.alreadyGenerated ? (
              <>
                <span className="qa-done">✓ Records generated</span>
                <button
                  type="button"
                  className="qa-gen danger"
                  disabled={busy}
                  onClick={() => void purge()}
                >
                  🗑 Delete generated data
                </button>
              </>
            ) : null}
            <button
              type="button"
              className="qa-gen"
              disabled={busy || !data?.run || !t.projects}
              title={
                !data?.run
                  ? 'No run selected'
                  : !t.projects
                    ? 'Choose a project above first'
                    : `Generate ${t.projects} project(s)`
              }
              onClick={() => void generate()}
            >
              🚀 Generate selected
            </button>
          </span>
        </div>
      </div>

      {/* ── The only scrolling region ──────────────────────────────────────────────── */}
      <div className="qa-body">
        {error ? <div className="qa-err">{error}</div> : null}
        {notice ? <div className="qa-note">{notice}</div> : null}

        {loading ? (
          <div className="qa-empty">Loading…</div>
        ) : !data?.run ? (
          /* Empty state 1 — nothing to browse at all. */
          <div className="qa-empty">
            No analysis run has produced a proposal yet. Run one from{' '}
            <strong>Program Quick Start</strong>.
          </div>
        ) : data.nothingChosen ? (
          /* Empty state 2 — a run is chosen but no project is. NOT the same as "no match". */
          <div className="qa-empty">
            Choose a <strong>project</strong> above — one, several with Ctrl/⌘-click, or{' '}
            <strong>All projects</strong> — to see what this run proposes.
          </div>
        ) : !data.programs.length ? (
          /* Empty state 3 — the filters genuinely matched nothing. */
          <div className="qa-empty">
            Nothing matches this selection. Widen the project or initiative filter, or press{' '}
            <strong>Reset filters</strong>.
          </div>
        ) : (
          /*
            THE PROPOSAL TREE IS THE SHARED ONE, NOT A SECOND BODY.

            The source renders this screen's body with a single line —
            `<%- include('_quick-start-review') %>` — the SAME partial the review screen
            includes. What was here instead was bespoke `qa-proj` / `qa-opp` markup, which is
            why the data looked nothing like the source: different rows, different chips, no
            selection tree, no drag handles.

            The give-away was in the stylesheet: `.qa-proj`, `.qa-chip`, `.qa-pi` and
            `.qa-grp-hd` are defined in that view's <style> block and appear ZERO times in the
            partial that actually renders the body — styling written for markup the source
            never emits. Corrected 2026-08-17.

            Read-only here: this screen INTERROGATES a run, it does not edit one. The tree's
            editing hooks are inert rather than removed, so the markup stays identical.
          */
          <QuickStartReviewBody
            programs={data.programs as Dict[]}
            proposal={(data.run?.proposal as Dict) ?? null}
            visible={visibleAll}
            genProg={EMPTY_SET}
            genOpp={EMPTY_SET}
            isOn={picker.isOn}
            setMany={picker.setMany}
            toggleProgram={picker.toggleProgram}
            toggleOpp={picker.toggleOpp}
            toggleDel={picker.toggleDel}
            dupWarn={EMPTY_DUP}
            resolutions={EMPTY_RES}
            setResolutions={NOOP}
            dropHint={picker.dropHint}
            dropBefore={picker.dropBefore}
            onItemDragStart={picker.onItemDragStart}
            onItemDragEnd={picker.onItemDragEnd}
            onPhaseDragOver={picker.onPhaseDragOver}
            onPhaseDrop={picker.onPhaseDrop}
          />
        )}
      </div>

      {/* ── Run info drawer — everything the run recorded ──────────────────────────── */}
      {drawer && data?.run ? (
        <div className="qa-ov" onClick={() => setDrawer(false)}>
          <div className="qa-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="qa-dh">
              <h3>Run #{s(data.run.id)}</h3>
              <button type="button" className="qa-dx" onClick={() => setDrawer(false)}>
                ×
              </button>
            </div>
            <div className="qa-db">
              <div className="qa-kv">
                <b>Source files</b>
                <span>{s(data.run.file_name) || '—'}</span>
              </div>
              <div className="qa-kv">
                <b>Analysed</b>
                <span>{s(data.run.created)}</span>
              </div>
              <div className="qa-kv">
                <b>Status</b>
                <span>{s(data.run.status)}</span>
              </div>
              <div className="qa-kv">
                <b>Proposes</b>
                <span>
                  {tree.length} project(s) ·{' '}
                  {tree.reduce((n, p) => n + p.opps.length, 0)} initiative(s)
                </span>
              </div>
              <div className="qa-kv">
                <b>Instructions</b>
                <span>{s(data.run.instructions) || '(none typed)'}</span>
              </div>
              <div className="qa-kv">
                <b>Reference files</b>
                <span>
                  {refFiles.length
                    ? refFiles.map((r) => `${r.name} — ${r.purpose || 'purpose not stated'}`).join('; ')
                    : '(none)'}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** One work item: badge, colour dot, name, phase, date. A missing date reads TBD. */
